import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react';
import api from '../api/client';
import { Championship, ChampionshipRound } from '../types';
import { SortDirection, getSortIndicator, sortRows } from '../utils/sort';
import './RaceResultsViewer.css';
import './PracticeTab.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PracticeBestLap {
  driverId?: number;
  driverName: string;
  firstName?: string;
  lastName?: string;
  carModel: string;
  raceNumber: string | number | null;
  bestLapMs: number | null;
  totalLaps: number;
  lastSessionDate: string;
}

interface CollisionDetail {
  collider: string;
  hasDamage: boolean;
  impactKmh: number | null;
  serverTimestampMs: number | null;
}

interface IngamePenalty {
  type: string;
  timeMs: number | null;
  investigationType: string | null;
  isPending: boolean;
  clearedAtMs: number | null;
}

interface LapDetail {
  lapNumber: number;
  sector1: number | null;
  sector2: number | null;
  sector3: number | null;
  lapTime: number | null;
  isInvalid: boolean;
  flags: string[];
  collisions: CollisionDetail[];
  ingamePenalties: IngamePenalty[];
}

interface SessionLaps {
  sessionId: number;
  sessionType: string;
  sessionName: string;
  serverName: string;
  trackName: string;
  trackLayoutName: string;
  sessionDate: string;
  laps: LapDetail[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

// VALID and INVALID are not shown as icons — they colour the lap row instead.
const FLAG_COLORS: Record<string, { bg?: string; label: string; pattern?: boolean }> = {
  WHITE:              { bg: '#e2e8f0', label: 'White' },
  GREEN:              { bg: '#22c55e', label: 'Green' },
  RED:                { bg: '#ef4444', label: 'Red' },
  BLUE:               { bg: '#3b82f6', label: 'Blue' },
  YELLOW:             { bg: '#eab308', label: 'Yellow' },
  CHECKERED:          { label: 'Checkered', pattern: true },
  BLACK_WHITE:        { label: 'Black/White', pattern: true },
  ORANGE_CIRCLE:      { label: 'Orange Circle', pattern: true },
  RED_YELLOW_STRIPES: { label: 'Red/Yellow Stripes', pattern: true },
  PITSTOP:            { label: 'Pit stop', pattern: true },
};

const FLAG_CLASS: Record<string, string> = {
  CHECKERED:          'flag-checkered',
  BLACK_WHITE:        'flag-black-white',
  ORANGE_CIRCLE:      'flag-orange-circle',
  RED_YELLOW_STRIPES: 'flag-red-yellow-stripes',
  PITSTOP:            'flag-pitstop',
};

const PENALTY_LABELS: Record<string, string> = {
  PenaltyType_Warning:                   'Warning',
  PenaltyType_Time_Penalty_Seconds:      'Time Penalty',
  PenaltyType_Drive_Through:             'Drive Through',
  PenaltyType_Stop_And_Go:               'Stop & Go',
  PenaltyType_Disqualification:          'DSQ',
  PenaltyType_MP_TeleportToPit:          'Teleport to Pit',
  PenaltyType_Black_Flag_With_Slow_Down: 'Slow Down',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (seconds: number | null): string => {
  if (seconds === null) return '—';
  const totalSeconds = Math.floor(seconds);
  const ms = Math.round((seconds - totalSeconds) * 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}:${String(secs).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
  }
  return `${secs}.${String(ms).padStart(3, '0')}`;
};

const formatTimeMs = (ms: number | null): string => {
  if (ms === null) return '—';
  return formatTime(ms / 1000);
};

const displayName = (row: PracticeBestLap): string =>
  row.firstName && row.lastName ? `${row.firstName} ${row.lastName}` : row.driverName;

const penaltyLabel = (type: string): string =>
  PENALTY_LABELS[type] || type.replace(/^PenaltyType_/, '').replace(/_/g, ' ');

// ─── Sub-components ───────────────────────────────────────────────────────────

const FlagIcon: React.FC<{ flag: string }> = ({ flag }) => {
  const meta = FLAG_COLORS[flag];
  if (!meta) return null;
  const patternClass = FLAG_CLASS[flag] ?? '';
  return (
    <span
      className={`flag-icon ${patternClass}`}
      style={meta.bg ? { background: meta.bg } : undefined}
      title={meta.label}
    />
  );
};

const CollisionBadge: React.FC<{ col: CollisionDetail }> = ({ col }) => {
  const colourClass = col.hasDamage ? 'collision-damage' : 'collision-no-damage';
  const title = [
    `${col.collider} impact`,
    col.impactKmh != null ? `${col.impactKmh.toFixed(1)} km/h` : null,
    col.hasDamage ? 'DAMAGE' : 'no damage',
  ].filter(Boolean).join(' · ');
  return (
    <span className={`collision-badge ${colourClass}`} title={title}>
      <AlertTriangle size={11} />
      {col.collider[0]}
    </span>
  );
};

const PenaltyBadge: React.FC<{ pen: IngamePenalty }> = ({ pen }) => {
  const label = penaltyLabel(pen.type);
  const timeLabel = pen.timeMs != null && pen.timeMs > 0 ? ` +${pen.timeMs / 1000}s` : '';
  const title = [
    label + timeLabel,
    pen.investigationType ? `Investigation: ${pen.investigationType}` : null,
    pen.isPending ? 'Pending' : 'Cleared',
  ].filter(Boolean).join(' · ');
  return (
    <span className={`ingame-penalty-badge ${pen.isPending ? 'pending' : 'cleared'}`} title={title}>
      {label}{timeLabel}
    </span>
  );
};

// ─── Lap highlights helper ────────────────────────────────────────────────────

type LapWithHighlights = LapDetail & {
  isBestSoFarSector1: boolean;
  isBestSoFarSector2: boolean;
  isBestSoFarSector3: boolean;
  isBestSoFarLap: boolean;
  isOverallBestSector1: boolean;
  isOverallBestSector2: boolean;
  isOverallBestSector3: boolean;
  isOverallBestLapTime: boolean;
};

const computeHighlights = (laps: LapDetail[], obS1: number | null, obS2: number | null, obS3: number | null, obLap: number | null): LapWithHighlights[] => {
  let bS1: number | null = null, bS2: number | null = null, bS3: number | null = null, bLap: number | null = null;
  return laps.map((lap) => {
    const bs1 = lap.sector1 !== null && (bS1 === null || lap.sector1 < bS1);
    const bs2 = lap.sector2 !== null && (bS2 === null || lap.sector2 < bS2);
    const bs3 = lap.sector3 !== null && (bS3 === null || lap.sector3 < bS3);
    const bsLap = lap.lapTime !== null && (bLap === null || lap.lapTime < bLap);
    if (bs1) bS1 = lap.sector1;
    if (bs2) bS2 = lap.sector2;
    if (bs3) bS3 = lap.sector3;
    if (bsLap) bLap = lap.lapTime;
    return {
      ...lap,
      isBestSoFarSector1: bs1, isBestSoFarSector2: bs2, isBestSoFarSector3: bs3, isBestSoFarLap: bsLap,
      isOverallBestSector1: lap.sector1 !== null && obS1 !== null && lap.sector1 === obS1,
      isOverallBestSector2: lap.sector2 !== null && obS2 !== null && lap.sector2 === obS2,
      isOverallBestSector3: lap.sector3 !== null && obS3 !== null && lap.sector3 === obS3,
      isOverallBestLapTime: lap.lapTime !== null && obLap !== null && lap.lapTime === obLap,
    };
  });
};

// ─── Driver laps modal ────────────────────────────────────────────────────────

type LapSortKey = 'lap' | 'sector1' | 'sector2' | 'sector3' | 'lapTime' | 'flags';

const DriverLapsModal: React.FC<{
  driver: PracticeBestLap;
  serverId: string;
  trackName?: string;
  trackLayoutName?: string;
  onClose: () => void;
}> = ({ driver, serverId, trackName, trackLayoutName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionLaps[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set());
  const [lapSortConfig, setLapSortConfig] = useState<{ key: LapSortKey; direction: SortDirection }>({ key: 'lap', direction: 'asc' });

  // Pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!driver.driverId) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.getPracticeDriverLaps(serverId, driver.driverId!, trackName, trackLayoutName);
        const data: SessionLaps[] = res.data.sessions || [];
        setSessions(data);
        // Expand all sessions by default
        setExpandedSessions(new Set(data.map((s) => s.sessionId)));
      } catch (err) {
        setError('Failed to load lap data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [driver.driverId, serverId, trackName, trackLayoutName]);

  // Compute overall bests across ALL sessions
  const { obS1, obS2, obS3, obLap } = useMemo(() => {
    const allLaps = sessions.flatMap((s) => s.laps);
    return {
      obS1: allLaps.reduce<number | null>((b, l) => l.sector1 === null ? b : b === null || l.sector1 < b ? l.sector1 : b, null),
      obS2: allLaps.reduce<number | null>((b, l) => l.sector2 === null ? b : b === null || l.sector2 < b ? l.sector2 : b, null),
      obS3: allLaps.reduce<number | null>((b, l) => l.sector3 === null ? b : b === null || l.sector3 < b ? l.sector3 : b, null),
      obLap: allLaps.reduce<number | null>((b, l) => l.lapTime === null ? b : b === null || l.lapTime < b ? l.lapTime : b, null),
    };
  }, [sessions]);

  const allLapRows = useMemo(() => {
    return sessions.flatMap((sess) =>
      computeHighlights(sess.laps, obS1, obS2, obS3, obLap).map((lap) => ({
        ...lap,
        sessionId: sess.sessionId,
        sessionDate: sess.sessionDate,
        trackName: sess.trackName,
        trackLayoutName: sess.trackLayoutName,
      }))
    );
  }, [sessions, obS1, obS2, obS3, obLap]);

  const handleLapSort = (key: LapSortKey) => {
    setLapSortConfig((c) => c.key === key ? { key, direction: c.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' });
    setPage(0);
  };

  const toggleSession = (id: number) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const driverDisplayName = displayName(driver);

  return (
    <div className="lap-details-modal-overlay" onClick={onClose}>
      <div className="lap-details-modal practice-driver-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lap-details-header">
          <div>
            <h3>{driverDisplayName}</h3>
            <p>{driver.carModel}{driver.raceNumber != null ? ` · #${driver.raceNumber}` : ''}</p>
          </div>
          <button className="close-button" onClick={onClose}><X size={20} /></button>
        </div>

        {loading && <div className="lap-details-empty">Loading lap data...</div>}
        {error && <div className="lap-details-empty error">{error}</div>}

        {!loading && !error && sessions.length === 0 && (
          <div className="lap-details-empty">No lap data found.</div>
        )}

        {!loading && !error && sessions.length > 0 && (
          <div className="lap-details-table-wrapper">
            {sessions.map((sess) => {
              const isExpanded = expandedSessions.has(sess.sessionId);
              const highlights = computeHighlights(sess.laps, obS1, obS2, obS3, obLap);

              const sortedLaps = sortRows(highlights, lapSortConfig.direction, (lap) => {
                switch (lapSortConfig.key) {
                  case 'lap': return lap.lapNumber;
                  case 'sector1': return lap.sector1 ?? Infinity;
                  case 'sector2': return lap.sector2 ?? Infinity;
                  case 'sector3': return lap.sector3 ?? Infinity;
                  case 'lapTime': return lap.lapTime ?? Infinity;
                  case 'flags': return lap.flags.join(',');
                  default: return lap.lapNumber;
                }
              });

              const sessionLabel = sess.sessionDate
                ? new Date(sess.sessionDate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                : `Session ${sess.sessionId}`;

              return (
                <div key={sess.sessionId} className="practice-session-group">
                  <button
                    type="button"
                    className="practice-session-header"
                    onClick={() => toggleSession(sess.sessionId)}
                  >
                    <span className="practice-session-label">
                      {sessionLabel} — {sess.trackName}{sess.trackLayoutName ? ` / ${sess.trackLayoutName}` : ''}
                    </span>
                    <span className="practice-session-meta">
                      {sess.laps.length} lap{sess.laps.length !== 1 ? 's' : ''}
                    </span>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>

                  {isExpanded && (
                    <table className="lap-details-table">
                      <thead>
                        <tr>
                          <th><button type="button" className="sort-btn" onClick={() => handleLapSort('lap')}>Lap {getSortIndicator(lapSortConfig.key, 'lap', lapSortConfig.direction)}</button></th>
                          <th><button type="button" className="sort-btn" onClick={() => handleLapSort('sector1')}>Sector 1 {getSortIndicator(lapSortConfig.key, 'sector1', lapSortConfig.direction)}</button></th>
                          <th><button type="button" className="sort-btn" onClick={() => handleLapSort('sector2')}>Sector 2 {getSortIndicator(lapSortConfig.key, 'sector2', lapSortConfig.direction)}</button></th>
                          <th><button type="button" className="sort-btn" onClick={() => handleLapSort('sector3')}>Sector 3 {getSortIndicator(lapSortConfig.key, 'sector3', lapSortConfig.direction)}</button></th>
                          <th><button type="button" className="sort-btn" onClick={() => handleLapSort('lapTime')}>Lap Time {getSortIndicator(lapSortConfig.key, 'lapTime', lapSortConfig.direction)}</button></th>
                          <th><button type="button" className="sort-btn" onClick={() => handleLapSort('flags')}>Flags {getSortIndicator(lapSortConfig.key, 'flags', lapSortConfig.direction)}</button></th>
                          <th>Events</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLaps.map((lap) => {
                          const hasEvents = lap.collisions.length > 0 || lap.ingamePenalties.length > 0;
                          return (
                            <tr key={`${sess.sessionId}-${lap.lapNumber}`} className={lap.isInvalid ? 'lap-row-invalid' : ''}>
                              <td>{lap.lapNumber}</td>
                              <td>
                                <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestSector1 ? 'overall-best' : lap.isBestSoFarSector1 ? 'best-so-far' : ''}`}>
                                  {formatTime(lap.sector1)}
                                </span>
                              </td>
                              <td>
                                <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestSector2 ? 'overall-best' : lap.isBestSoFarSector2 ? 'best-so-far' : ''}`}>
                                  {formatTime(lap.sector2)}
                                </span>
                              </td>
                              <td>
                                <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestSector3 ? 'overall-best' : lap.isBestSoFarSector3 ? 'best-so-far' : ''}`}>
                                  {formatTime(lap.sector3)}
                                </span>
                              </td>
                              <td>
                                <span className={`lap-value ${lap.isInvalid ? 'invalid' : lap.isOverallBestLapTime ? 'overall-best' : lap.isBestSoFarLap ? 'best-so-far' : ''}`}>
                                  {formatTime(lap.lapTime)}
                                </span>
                              </td>
                              <td>
                                <div className="lap-flags">
                                  {lap.flags.length > 0
                                    ? lap.flags.map((f) => <FlagIcon key={f} flag={f} />)
                                    : <span className="lap-value">—</span>}
                                </div>
                              </td>
                              <td>
                                {hasEvents ? (
                                  <div className="lap-events">
                                    {lap.collisions.map((col, i) => <CollisionBadge key={i} col={col} />)}
                                    {lap.ingamePenalties.map((pen, i) => <PenaltyBadge key={i} pen={pen} />)}
                                  </div>
                                ) : (
                                  <span className="lap-value">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── PracticeTab ──────────────────────────────────────────────────────────────

type BestLapSortKey = 'driver' | 'carNumber' | 'car' | 'bestLap' | 'totalLaps';

interface PracticeTabProps {
  championship: Championship;
  servers: any[];
  rounds: ChampionshipRound[];
}

const PracticeTab: React.FC<PracticeTabProps> = ({ championship, servers, rounds }) => {
  const [bestLaps, setBestLaps] = useState<PracticeBestLap[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<PracticeBestLap | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: BestLapSortKey; direction: SortDirection }>({ key: 'bestLap', direction: 'asc' });

  // Find practice server(s): same championshipId, game_type is practice
  const practiceServers = useMemo(
    () => servers.filter((s) => s.championshipId === championship.id && s.seasonConfig?.game_type === 'GameModeType_PRACTICE'),
    [servers, championship.id]
  );

  const practiceServerIds = useMemo(() => practiceServers.map((s: any) => s.id as string), [practiceServers]);

  // Determine the current event's track: find the next upcoming round, or fall back to the most recent past round
  const currentTrack = useMemo((): { trackName: string; trackLayoutName: string } | null => {
    if (rounds.length === 0) return null;
    const now = new Date();
    // Find first round whose date/time is in the future (upcoming event)
    const upcoming = rounds.find((r) => r.date && r.startTime && new Date(`${r.date}T${r.startTime}`) > now);
    const target = upcoming ?? rounds[rounds.length - 1];
    if (!target?.track) return null;
    return { trackName: target.track, trackLayoutName: target.layout ?? '' };
  }, [rounds]);

  const load = useCallback(async () => {
    if (practiceServerIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const trackName = currentTrack?.trackName;
      const trackLayoutName = currentTrack?.trackLayoutName;
      const responses = await Promise.all(
        practiceServerIds.map((id) => api.getPracticeBestLaps(id, trackName, trackLayoutName))
      );
      const all: PracticeBestLap[] = responses.flatMap((r) => r.data.bestLaps || []);

      setBestLaps(all);
    } catch (err) {
      setError('Failed to load practice data.');
    } finally {
      setLoading(false);
    }
  }, [practiceServerIds, currentTrack]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (key: BestLapSortKey) => {
    setSortConfig((c) => c.key === key ? { key, direction: c.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' });
  };

  const sortedRows = useMemo(() => sortRows(bestLaps, sortConfig.direction, (row) => {
    switch (sortConfig.key) {
      case 'driver': return displayName(row);
      case 'carNumber': return row.raceNumber != null ? Number(row.raceNumber) : Infinity;
      case 'car': return row.carModel;
      case 'bestLap': return row.bestLapMs ?? Infinity;
      case 'totalLaps': return row.totalLaps;
      default: return '';
    }
  }), [bestLaps, sortConfig]);

  // Overall best lap for purple highlight
  const overallBestMs = useMemo(
    () => bestLaps.reduce<number | null>((b, r) => r.bestLapMs === null ? b : b === null || r.bestLapMs < b ? r.bestLapMs : b, null),
    [bestLaps]
  );

  const SortBtn: React.FC<{ colKey: BestLapSortKey; label: string }> = ({ colKey, label }) => (
    <button type="button" className="sort-btn" onClick={() => handleSort(colKey)}>
      {label} <span aria-hidden="true">{getSortIndicator(sortConfig.key, colKey, sortConfig.direction)}</span>
    </button>
  );

  if (practiceServerIds.length === 0) {
    return (
      <div className="practice-tab-empty">
        <p>No practice server linked to this championship.</p>
        <p className="practice-tab-hint">A practice server is automatically created alongside the race server when you set up championship servers. Results from that server will appear here.</p>
      </div>
    );
  }

  // Find the primary practice server for drill-down (first one)
  const primaryServerId = practiceServerIds[0];

  return (
    <div className="practice-tab">
      {loading && <div className="empty-state">Loading practice data...</div>}
      {error && <div className="empty-state">{error}</div>}

      {!loading && !error && (
        <>
          <div className="practice-tab-header">
            <span className="practice-tab-count">{sortedRows.length} entr{sortedRows.length !== 1 ? 'ies' : 'y'}</span>
            {currentTrack && (
              <span className="practice-tab-track">
                {currentTrack.trackName}{currentTrack.trackLayoutName ? ` / ${currentTrack.trackLayoutName}` : ''}
              </span>
            )}
            <span className="practice-tab-hint">Best lap per driver/car combination across all practice sessions on this track · click a row to view lap breakdown</span>
          </div>

          <div className="practice-sort-bar">
            <span className="practice-sort-label">Sort by</span>
            <select
              className="practice-sort-select"
              value={`${sortConfig.key}:${sortConfig.direction}`}
              onChange={(e) => {
                const [k, d] = e.target.value.split(':') as [BestLapSortKey, SortDirection];
                setSortConfig({ key: k, direction: d });
              }}
            >
              <option value="bestLap:asc">Best Lap (fastest first)</option>
              <option value="bestLap:desc">Best Lap (slowest first)</option>
              <option value="driver:asc">Driver (A → Z)</option>
              <option value="driver:desc">Driver (Z → A)</option>
              <option value="car:asc">Car (A → Z)</option>
              <option value="car:desc">Car (Z → A)</option>
              <option value="totalLaps:desc">Most Laps</option>
              <option value="totalLaps:asc">Fewest Laps</option>
            </select>
          </div>

          <div className="table-wrapper">
            <table className="practice-table">
              <thead>
                <tr>
                  <th className="col-pos">#</th>
                  <th><SortBtn colKey="driver" label="Driver" /></th>
                  <th><SortBtn colKey="carNumber" label="Car #" /></th>
                  <th><SortBtn colKey="car" label="Car" /></th>
                  <th className="col-time"><SortBtn colKey="bestLap" label="Best Lap" /></th>
                  <th className="col-num"><SortBtn colKey="totalLaps" label="Total Laps" /></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, idx) => {
                  const isOverallBest = row.bestLapMs !== null && overallBestMs !== null && row.bestLapMs === overallBestMs;
                  const canDrillDown = row.driverId != null;
                  return (
                    <tr
                      key={`${row.driverId ?? row.driverName}__${row.carModel}`}
                      className={`practice-row ${isOverallBest ? 'best-lap' : ''} ${canDrillDown ? 'clickable' : ''}`}
                      onClick={() => canDrillDown && setSelectedDriver(row)}
                    >
                      <td className="col-pos" data-label="#">{idx + 1}</td>
                      <td data-label="Driver">
                        <span className="practice-driver-cell">
                          <span className={`practice-driver-name ${canDrillDown ? 'driver-link' : ''}`}>
                            {displayName(row)}
                          </span>
                          {row.raceNumber != null && (
                            <span className="practice-inline-number">#{row.raceNumber}</span>
                          )}
                        </span>
                      </td>
                      <td className="col-num" data-label="Car #">
                        {row.raceNumber != null
                          ? <span className="race-number">#{row.raceNumber}</span>
                          : <span className="lap-value">—</span>}
                      </td>
                      <td data-label="Car"><span className="car-model-cell">{row.carModel}</span></td>
                      <td className="col-time" data-label="Best Lap">
                        <span className={`lap-value mono ${isOverallBest ? 'overall-best' : ''}`}>
                          {formatTimeMs(row.bestLapMs)}
                        </span>
                      </td>
                      <td className="col-num" data-label="Total Laps">
                        <span className="lap-value">{Number(row.totalLaps)}</span>
                      </td>
                    </tr>
                  );
                })}
                {sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty-state">No practice laps recorded yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedDriver && (
        <DriverLapsModal
          driver={selectedDriver}
          serverId={primaryServerId}
          trackName={currentTrack?.trackName}
          trackLayoutName={currentTrack?.trackLayoutName}
          onClose={() => setSelectedDriver(null)}
        />
      )}
    </div>
  );
};

export default PracticeTab;
